import React, { useState, useEffect } from 'react';
import api from '../../../services/api';
import { useI18n } from '../../../i18n/I18nContext';

const Icon = ({ name, className }) => {
    const map = {
        'truck': 'bi bi-truck',
        'money': 'bi bi-cash-stack',
        'chart': 'bi bi-graph-up-arrow',
        'spinner': 'bi bi-arrow-repeat spinning',
        'info': 'bi bi-info-circle',
        'check': 'bi bi-check-circle-fill',
        'alert': 'bi bi-exclamation-triangle-fill',
        'refresh': 'bi bi-arrow-clockwise',
        'fuel': 'bi bi-fuel-pump-fill',
        'geo': 'bi bi-geo-alt-fill',
        'wallet': 'bi bi-wallet2',
        'close': 'bi bi-x-lg',
        'person': 'bi bi-person-badge',
        'speedometer': 'bi bi-speedometer2'
    };
    return <i className={`${map[name] || `bi bi-${name}`} ${className || ''}`} />;
};

const ProfitabilityTab = () => {
    const { t } = useI18n();
    const [subTab, setSubTab] = useState('mission'); // 'mission' or 'vehicle'
    
    // Dropdown selections
    const [selectedMissionId, setSelectedMissionId] = useState('');
    const [selectedVehicleId, setSelectedVehicleId] = useState('');
    const [otherCharges, setOtherCharges] = useState(0);
    const [targetMargin, setTargetMargin] = useState(30);

    // Global dashboard store
    const [dashboard, setDashboard] = useState(null);
    const [loadingDashboard, setLoadingDashboard] = useState(false);

    const getMissionLabel = (m) => {
        if (!m) return '';
        const departureStr = m.expediteur_nom || m.expediteurNom || m.departure_location || m.departureLocation;
        const arrivalStr = m.destinataire_nom || m.destinataireNom || m.arrival_location || m.arrivalLocation;
        let label = `${departureStr || '?'} → ${arrivalStr || '?'}`;
        if (m.expediteur_nom || m.expediteurNom || m.destinataire_nom || m.destinataireNom) {
            label += ` (${m.departure_location || m.departureLocation || '?'} → ${m.arrival_location || m.arrivalLocation || '?'})`;
        }
        return label;
    };

    // Results states
    const [calculating, setCalculating] = useState(false);
    const [missionResult, setMissionResult] = useState(null);
    const [vehicleResult, setVehicleResult] = useState(null);
    const [error, setError] = useState(null);
    const [rawResponse, setRawResponse] = useState(null);

    // Load light dashboard on mount (filtered dynamically for current logged-in transporteur by Java/Python backend)
    useEffect(() => {
        loadGlobalDashboard();
    }, []);

    // Clear results when tabs change
    useEffect(() => {
        setSelectedMissionId('');
        setSelectedVehicleId('');
        setMissionResult(null);
        setVehicleResult(null);
        setRawResponse(null);
    }, [subTab]);

    const loadGlobalDashboard = async () => {
        setLoadingDashboard(true);
        setError(null);
        try {
            const res = await api.get('/agent/planning/fraud/profitability/dashboard?light=true');
            if (res.data?.ok && res.data?.data) {
                setDashboard(res.data.data);
            }
        } catch (e) {
            console.error("Erreur chargement tableau de bord global:", e);
            setError("Impossible de charger les données de rentabilité.");
        } finally {
            setLoadingDashboard(false);
        }
    };

    const handleCalculateMission = async () => {
        if (!selectedMissionId) return;
        setCalculating(true);
        setMissionResult(null);
        setRawResponse(null);
        setError(null);
        try {
            const payload = {
                mission_id: selectedMissionId,
                use_ai: false,
                manual_other_charges: Number(otherCharges),
                target_margin_pct: Number(targetMargin)
            };
            const res = await api.post('/agent/planning/fraud/pricing/analyze', payload);
            setRawResponse(res.data);
            
            if (res.data?.ok === false) {
                setError(res.data?.error || "Erreur lors de l'analyse.");
            } else {
                setMissionResult(res.data?.data || res.data);
            }
        } catch (e) {
            console.error("Erreur lors de l'analyse mission:", e);
            setError("Erreur technique lors du calcul.");
        } finally {
            setCalculating(false);
        }
    };

    const handleCalculateVehicle = async () => {
        if (!selectedVehicleId) return;
        setCalculating(true);
        setVehicleResult(null);
        setRawResponse(null);
        setError(null);
        try {
            const queryParams = new URLSearchParams({
                light: 'false',
                vehiculeId: selectedVehicleId
            }).toString();

            const res = await api.get(`/agent/planning/fraud/profitability/dashboard?${queryParams}`);
            setRawResponse(res.data);

            if (res.data?.ok && res.data?.data?.ok) {
                const item = res.data.data.vehicles?.[0];
                if (item) {
                    setVehicleResult(item);
                } else {
                    setError("Aucune donnée trouvée pour ce véhicule.");
                }
            } else {
                setError(res.data?.data?.error || "Erreur lors de la récupération des statistiques du véhicule.");
            }
        } catch (e) {
            console.error("Erreur lors de l'analyse véhicule:", e);
            setError("Erreur technique lors de la communication avec le service.");
        } finally {
            setCalculating(false);
        }
    };

    const renderMissionResult = () => {
        if (!missionResult) return null;

        const costs = missionResult.costs || {};
        const prof = missionResult.profitability || {};
        const dist = missionResult.distance || {};
        const conso = missionResult.consumption || {};
        const fuel = missionResult.fuel_price || {};
        const currency = missionResult.currency || 'MAD';
        const warnings = Array.isArray(missionResult.warnings) ? missionResult.warnings : [];

        return (
            <div className="fade-in">
                {/* 4 Cards Grid */}
                <div className="row g-3 mb-4">
                    <div className="col-6 col-md-3">
                        <div className="p-3 bg-light border rounded-4 h-100 shadow-sm">
                            <div className="text-muted small fw-semibold text-uppercase mb-1">Prix mission</div>
                            <div className="h4 fw-bold text-dark mb-0">
                                {missionResult.price?.toLocaleString() || '0'} {currency}
                            </div>
                        </div>
                    </div>
                    <div className="col-6 col-md-3">
                        <div className="p-3 bg-light border rounded-4 h-100 shadow-sm">
                            <div className="text-muted small fw-semibold text-uppercase mb-1">Coût carburant</div>
                            <div className="h4 fw-bold text-dark mb-0">
                                {costs.fuel_cost?.toLocaleString() || '0'} {currency}
                            </div>
                        </div>
                    </div>
                    <div className="col-6 col-md-3">
                        <div className="p-3 bg-light border rounded-4 h-100 shadow-sm">
                            <div className="text-muted small fw-semibold text-uppercase mb-1">Charges saisies</div>
                            <div className="h4 fw-bold text-dark mb-0">
                                {costs.manual_other_charges?.toLocaleString() || '0'} {currency}
                            </div>
                        </div>
                    </div>
                    <div className="col-6 col-md-3">
                        <div className="p-3 bg-primary-soft border border-primary-subtle rounded-4 h-100 shadow-sm">
                            <div className="text-primary small fw-semibold text-uppercase mb-1">Rentabilité</div>
                            <div className="h4 fw-bold text-primary mb-0">
                                {prof.profitability_pct || '0'}%
                            </div>
                        </div>
                    </div>
                </div>

                {/* 6 Info Boxes Grid */}
                <div className="row g-3 mb-4">
                    <div className="col-md-4">
                        <div className="bg-light border p-3 rounded-4 shadow-sm">
                            <div className="text-muted small mb-1">Mission</div>
                            <div className="fw-semibold text-dark text-truncate">{missionResult.reference_number || missionResult.mission_id || '—'}</div>
                        </div>
                    </div>
                    <div className="col-md-4">
                        <div className="bg-light border p-3 rounded-4 shadow-sm">
                            <div className="text-muted small mb-1">Verdict</div>
                            <div className="fw-semibold text-dark">{prof.label || '—'}</div>
                        </div>
                    </div>
                    <div className="col-md-4">
                        <div className="bg-light border p-3 rounded-4 shadow-sm">
                            <div className="text-muted small mb-1">Bénéfice estimé</div>
                            <div className="fw-semibold text-dark">{prof.profit_amount?.toLocaleString() || '0'} {currency}</div>
                        </div>
                    </div>
                    <div className="col-md-4">
                        <div className="bg-light border p-3 rounded-4 shadow-sm">
                            <div className="text-muted small mb-1">Coût connu total</div>
                            <div className="fw-semibold text-dark">{costs.total_known_cost?.toLocaleString() || '0'} {currency}</div>
                        </div>
                    </div>
                    <div className="col-md-4">
                        <div className="bg-light border p-3 rounded-4 shadow-sm">
                            <div className="text-muted small mb-1">Distance</div>
                            <div className="fw-semibold text-dark">{dist.distance_km || '0'} km • {dist.source || '—'}</div>
                        </div>
                    </div>
                    <div className="col-md-4">
                        <div className="bg-light border p-3 rounded-4 shadow-sm">
                            <div className="text-muted small mb-1">Conso / carburant</div>
                            <div className="fw-semibold text-dark">{(conso.consumption_l_100km || 0).toFixed(1)} L/100 • {fuel.fuel_price_per_l?.toFixed(2)} /L</div>
                        </div>
                    </div>
                </div>

                {/* Alert/Warning Section */}
                <div className="mb-4">
                    {warnings.length ? (
                        <div className="d-flex flex-column gap-2">
                            {warnings.slice(0, 6).map((w, idx) => (
                                <div key={idx} className="p-3 bg-danger-soft border border-danger-subtle rounded-4 d-flex align-items-start gap-2 text-danger small">
                                    <Icon name="alert" className="mt-0.5" />
                                    <span>{w}</span>
                                </div>
                            ))}
                        </div>
                    ) : (
                        <div className="p-3 bg-primary-soft border border-primary-subtle rounded-4 text-dark text-center small">
                            Alerte: Autres charges non saisies: calcul basé seulement sur carburant.
                        </div>
                    )}
                </div>
            </div>
        );
    };

    const renderVehicleResult = () => {
        if (!vehicleResult) return null;

        const getClassement = (score) => {
            if (!score && score !== 0) return 'Non classé';
            if (score < 25) return 'Peu rentable / critique';
            if (score < 50) return 'Moyen';
            if (score < 75) return 'Rentable';
            return 'Très rentable';
        };

        const scoreJour = vehicleResult.score_day || vehicleResult.daily_score || vehicleResult.score_today || 0;
        const scoreMoyen = vehicleResult.score_avg_7d || vehicleResult.score_7d_avg || 0;
        
        const plate = vehicleResult.plate_number || vehicleResult.plaque || '—';
        const brand = vehicleResult.brand || vehicleResult.marque || '—';
        const year = vehicleResult.year || vehicleResult.annee || vehicleResult.annee_fabrication || '—';
        const transporteur = vehicleResult.transporteur_name || vehicleResult.entrepriseName || vehicleResult.transporteur_id || '—';
        
        const fraudScore = vehicleResult.fraud_score_event || vehicleResult.fraud_score || 0;
        const conso = vehicleResult.consommation_moyenne_l_100km || vehicleResult.avg_consumption_l_per_100km || 0;
        const costKm = vehicleResult.fuel_cost_km || vehicleResult.cost_per_km || 0;
        const breakdowns = vehicleResult.nb_pannes_day || vehicleResult.breakdowns_today || 0;
        const missions = vehicleResult.missions_count_day || vehicleResult.missions_today || vehicleResult.missions_count || 0;
        const breakdownRate = vehicleResult.taux_pannes_day || vehicleResult.breakdown_rate || 0;
        const age = vehicleResult.age_vehicule_annees || vehicleResult.vehicle_age || 0;

        const scoreJourClassement = vehicleResult.profitability_label || getClassement(scoreJour);
        const scoreMoyenClassement = vehicleResult.profitability_label_7d || getClassement(scoreMoyen);

        return (
            <div className="fade-in mt-2 p-4 rounded-4 bg-white border shadow-sm">
                <div className="d-flex align-items-center mb-3">
                    <span className="badge bg-danger-soft text-danger border border-danger-subtle rounded-pill px-3 py-2 fw-bold me-3" style={{ fontSize: '0.9rem' }}>
                        Score jour: {Number(scoreJour).toFixed(2)}
                    </span>
                </div>
                <h5 className="mb-4 fw-bold text-dark">
                    Classement rentabilité: {scoreJourClassement}
                </h5>

                <div className="row g-3">
                    {/* Row 1 */}
                    <div className="col-md-6">
                        <div className="p-3 bg-light border rounded-4 h-100">
                            <div className="text-muted small mb-1">Plaque</div>
                            <div className="fw-bold text-dark fs-5">{plate}</div>
                        </div>
                    </div>
                    <div className="col-md-6">
                        <div className="p-3 bg-light border rounded-4 h-100">
                            <div className="text-muted small mb-1">Marque / Modèle</div>
                            <div className="fw-bold text-dark fs-5">{brand}</div>
                        </div>
                    </div>

                    {/* Row 2 */}
                    <div className="col-md-6">
                        <div className="p-3 bg-light border rounded-4 h-100">
                            <div className="text-muted small mb-1">Année</div>
                            <div className="fw-bold text-dark fs-5">{year}</div>
                        </div>
                    </div>
                    <div className="col-md-6">
                        <div className="p-3 bg-light border rounded-4 h-100">
                            <div className="text-muted small mb-1">Transporteur</div>
                            <div className="fw-bold text-dark fs-5">{transporteur}</div>
                        </div>
                    </div>

                    {/* Row 3 */}
                    <div className="col-md-6">
                        <div className="p-3 bg-light border rounded-4 h-100">
                            <div className="text-muted small mb-1">Score moyen 7 jours</div>
                            <div className="fw-bold text-dark fs-5">{Number(scoreMoyen).toFixed(2)}</div>
                        </div>
                    </div>
                    <div className="col-md-6">
                        <div className="p-3 bg-light border rounded-4 h-100">
                            <div className="text-muted small mb-1">Classement 7 jours</div>
                            <div className="fw-bold text-dark fs-5">{scoreMoyenClassement}</div>
                        </div>
                    </div>

                    {/* Row 4 */}
                    <div className="col-md-6">
                        <div className="p-3 bg-light border rounded-4 h-100">
                            <div className="text-muted small mb-1">Fraud score événement</div>
                            <div className="fw-bold text-dark fs-5">{Number(fraudScore).toFixed(2)}</div>
                        </div>
                    </div>
                    <div className="col-md-6">
                        <div className="p-3 bg-light border rounded-4 h-100">
                            <div className="text-muted small mb-1">Consommation moyenne L/100km</div>
                            <div className="fw-bold text-dark fs-5">{Number(conso).toFixed(3)}</div>
                        </div>
                    </div>

                    {/* Row 5 */}
                    <div className="col-md-6">
                        <div className="p-3 bg-light border rounded-4 h-100">
                            <div className="text-muted small mb-1">Coût carburant / km</div>
                            <div className="fw-bold text-dark fs-5">{Number(costKm).toFixed(4)}</div>
                        </div>
                    </div>
                    <div className="col-md-6">
                        <div className="p-3 bg-light border rounded-4 h-100">
                            <div className="text-muted small mb-1">Pannes du jour</div>
                            <div className="fw-bold text-dark fs-5">{breakdowns}</div>
                        </div>
                    </div>

                    {/* Row 6 */}
                    <div className="col-md-6">
                        <div className="p-3 bg-light border rounded-4 h-100">
                            <div className="text-muted small mb-1">Missions du jour</div>
                            <div className="fw-bold text-dark fs-5">{missions}</div>
                        </div>
                    </div>
                    <div className="col-md-6">
                        <div className="p-3 bg-light border rounded-4 h-100">
                            <div className="text-muted small mb-1">Taux de pannes</div>
                            <div className="fw-bold text-dark fs-5">{Number(breakdownRate).toFixed(3)}</div>
                        </div>
                    </div>

                    {/* Row 7 */}
                    <div className="col-md-6">
                        <div className="p-3 bg-light border rounded-4 h-100">
                            <div className="text-muted small mb-1">Âge véhicule</div>
                            <div className="fw-bold text-dark fs-5">{age}</div>
                        </div>
                    </div>
                </div>
            </div>
        );
    };

    return (
        <div className="profitability-tab fade-in">
            {/* Header & Sub-Tab Selector */}
            <div className="d-flex flex-wrap justify-content-between align-items-center gap-3 mb-4">
                <div className="dashboard-header mb-0">
                    <h2 className="fw-bold text-dark mb-1">
                        {subTab === 'mission' ? 'Rentabilité missions' : 'Suivi métier des véhicules'}
                    </h2>
                    <p className="text-muted small mb-0">
                        {subTab === 'mission' 
                            ? "Choisis une mission avec prix. Rien n'est calculé tant que tu ne cliques pas sur calculer."
                            : "Choisis un véhicule. Cette partie analyse la rentabilité et les scores de performance."
                        }
                    </p>
                </div>
                <div className="d-flex gap-2">
                    <button 
                        className={`acc-btn-secondary py-1 px-3 rounded-pill fw-semibold ${subTab === 'mission' ? 'active-sub-tab border-primary text-primary' : 'text-muted'}`}
                        onClick={() => setSubTab('mission')}
                        style={{ padding: '0.4rem 1rem' }}
                    >
                        Missions
                    </button>
                    <button 
                        className={`acc-btn-secondary py-1 px-3 rounded-pill fw-semibold ${subTab === 'vehicle' ? 'active-sub-tab border-primary text-primary' : 'text-muted'}`}
                        onClick={() => setSubTab('vehicle')}
                        style={{ padding: '0.4rem 1rem' }}
                    >
                        Véhicules
                    </button>
                    <button 
                        className="acc-btn-secondary rounded-circle px-2 py-1"
                        onClick={loadGlobalDashboard} 
                        disabled={loadingDashboard}
                        title="Actualiser"
                        style={{ width: '2.5rem', height: '2.5rem', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                    >
                        <Icon name={loadingDashboard ? 'spinner' : 'refresh'} />
                    </button>
                </div>
            </div>

            {error && (
                <div className="alert alert-danger border-0 shadow-sm rounded-4 mb-4 d-flex align-items-center gap-2">
                    <Icon name="alert" className="fs-5" /> 
                    <span>{error}</span>
                </div>
            )}

            {subTab === 'mission' ? (
                <div className="row g-4">
                    {/* Left Column: 1. Sélection */}
                    <div className="col-lg-4">
                        <div className="acc-card border shadow-sm rounded-4 p-4 h-100">
                            <div className="acc-card-title mb-4">
                                <Icon name="money" /> 1. Sélection
                            </div>

                            {/* Mission avec prix select */}
                            <div className="acc-field-group mb-3">
                                <label><Icon name="truck" /> Mission avec prix</label>
                                <select 
                                    className="form-select py-2 px-3 border rounded-3"
                                    value={selectedMissionId}
                                    onChange={(e) => setSelectedMissionId(e.target.value)}
                                    disabled={loadingDashboard}
                                >
                                    {loadingDashboard ? (
                                        <option value="">Chargement des missions...</option>
                                    ) : (dashboard?.missions || []).length === 0 ? (
                                        <option value="">Aucune mission avec prix disponible</option>
                                    ) : (
                                        <>
                                            <option value="">-- Choisir une mission --</option>
                                            {(dashboard?.missions || []).map(m => (
                                                <option key={m.mission_id || m.id} value={m.mission_id || m.id}>
                                                    {getMissionLabel(m)}
                                                </option>
                                            ))}
                                        </>
                                    )}
                                </select>
                            </div>

                            {/* Autres charges manuelles */}
                            <div className="acc-field-group mb-3">
                                <label><Icon name="wallet" /> Autres charges manuelles</label>
                                <input 
                                    type="number" 
                                    className="form-control py-2 px-3 border rounded-3"
                                    value={otherCharges}
                                    onChange={(e) => setOtherCharges(e.target.value)}
                                    min="0" 
                                    step="0.01"
                                    placeholder="0 si inconnu"
                                />
                            </div>

                            {/* Marge cible (%) */}
                            <div className="acc-field-group mb-4">
                                <label><Icon name="chart" /> Marge cible (%)</label>
                                <input 
                                    type="number" 
                                    className="form-control py-2 px-3 border rounded-3"
                                    value={targetMargin}
                                    onChange={(e) => setTargetMargin(e.target.value)}
                                    min="0" 
                                    max="100"
                                    step="1"
                                    placeholder="30 par défaut"
                                />
                            </div>

                            {/* Action Button */}
                            <button 
                                type="button" 
                                className="acc-btn-primary w-100 py-2.5 rounded-3 fw-semibold"
                                onClick={handleCalculateMission}
                                disabled={calculating || !selectedMissionId}
                            >
                                {calculating ? (
                                    <><Icon name="spinner" /> Calcul...</>
                                ) : (
                                    <><Icon name="money" /> Calculer rentabilité</>
                                )}
                            </button>

                            {/* Data Source Box */}
                            <div className="p-3 bg-primary-soft border border-primary-subtle rounded-4 mt-4 text-dark">
                                <div className="fw-bold small mb-1"><Icon name="info" /> Source données</div>
                                <div className="text-muted extra-small" style={{ fontSize: '0.8rem', lineHeight: '1.4' }}>
                                    Distance, consommation et prix carburant sont cherchés automatiquement dans le backend, l’historique véhicule et les règles.
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Right Column: 2. Résultat */}
                    <div className="col-lg-8">
                        <div className="acc-card border shadow-sm rounded-4 p-4 h-100">
                            <div className="acc-card-title mb-4">
                                <Icon name="chart" /> 2. Résultat
                            </div>

                            {calculating ? (
                                <div className="d-flex flex-column align-items-center justify-content-center py-5 text-center text-muted">
                                    <Icon name="spinner" className="fs-1 text-primary mb-3" />
                                    <div className="fw-bold text-dark mb-1">Calcul en cours pour cette mission uniquement...</div>
                                    <div className="small">Récupération des trajectoires, du prix gasoil et de l'historique de consommation...</div>
                                </div>
                            ) : missionResult ? (
                                <>
                                    {renderMissionResult()}

                                    {/* Technical details Collapsible */}
                                    <details className="mt-4 pt-3 border-top">
                                        <summary className="text-muted small fw-bold cursor-pointer" style={{ listStyle: 'none', cursor: 'pointer' }}>
                                            ▶ Détail technique
                                        </summary>
                                        <pre className="bg-light p-3 rounded-3 mt-2 text-dark border overflow-auto" style={{ maxHeight: '250px', fontSize: '0.78rem' }}>
                                            {JSON.stringify(rawResponse, null, 2)}
                                        </pre>
                                    </details>
                                </>
                            ) : (
                                <div className="d-flex flex-column align-items-center justify-content-center py-5 text-center text-muted border border-dashed rounded-4">
                                    <Icon name="money" className="fs-1 mb-3 text-muted text-opacity-50" />
                                    <div className="small">Sélectionne une mission avec prix puis clique sur calculer.</div>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            ) : (
                <div className="row g-4">
                    {/* Left Column: 1. Sélection */}
                    <div className="col-lg-4">
                        <div className="acc-card border shadow-sm rounded-4 p-4 h-100">
                            <div className="acc-card-title mb-4">
                                <Icon name="truck" /> 1. Sélection
                            </div>

                            {/* Vehicle select */}
                            <div className="acc-field-group mb-4">
                                <label><Icon name="speedometer" /> Véhicule</label>
                                <select 
                                    className="form-select py-2 px-3 border rounded-3"
                                    value={selectedVehicleId}
                                    onChange={(e) => setSelectedVehicleId(e.target.value)}
                                    disabled={loadingDashboard}
                                >
                                    {loadingDashboard ? (
                                        <option value="">Chargement des véhicules...</option>
                                    ) : (dashboard?.vehicles || []).length === 0 ? (
                                        <option value="">Aucun véhicule disponible</option>
                                    ) : (
                                        <>
                                            <option value="">-- Choisir un véhicule --</option>
                                            {(dashboard?.vehicles || []).map(v => (
                                                <option key={v.vehicule_id || v.id} value={v.vehicule_id || v.id}>
                                                    {v.plate_number} • {v.brand || 'Sans marque'}
                                                </option>
                                            ))}
                                        </>
                                    )}
                                </select>
                            </div>

                            {/* Action Button */}
                            <button 
                                type="button" 
                                className="acc-btn-primary w-100 py-2.5 rounded-3 fw-semibold"
                                onClick={handleCalculateVehicle}
                                disabled={calculating || !selectedVehicleId}
                            >
                                {calculating ? (
                                    <><Icon name="spinner" /> Calcul...</>
                                ) : (
                                    <><Icon name="speedometer" /> Calculer performance</>
                                )}
                            </button>

                            {/* Data Source Box */}
                            <div className="p-3 bg-primary-soft border border-primary-subtle rounded-4 mt-4 text-dark">
                                <div className="fw-bold small mb-1"><Icon name="info" /> Source données</div>
                                <div className="text-muted extra-small" style={{ fontSize: '0.8rem', lineHeight: '1.4' }}>
                                    Les données kilométriques et les déclarations de carburant associées à ce véhicule sont agrégées sur le mois en cours.
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Right Column: 2. Résultat */}
                    <div className="col-lg-8">
                        <div className="acc-card border shadow-sm rounded-4 p-4 h-100">
                            <div className="acc-card-title mb-4">
                                <Icon name="chart" /> 2. Résultat
                            </div>

                            {calculating ? (
                                <div className="d-flex flex-column align-items-center justify-content-center py-5 text-center text-muted">
                                    <Icon name="spinner" className="fs-1 text-primary mb-3" />
                                    <div className="fw-bold text-dark mb-1">Calcul en cours pour ce véhicule uniquement...</div>
                                    <div className="small">Agrégation des pleins, calcul de la consommation moyenne pondérée...</div>
                                </div>
                            ) : vehicleResult ? (
                                <>
                                    {renderVehicleResult()}

                                    {/* Technical details Collapsible */}
                                    <details className="mt-4 pt-3 border-top">
                                        <summary className="text-muted small fw-bold cursor-pointer" style={{ listStyle: 'none', cursor: 'pointer' }}>
                                            ▶ Détail technique
                                        </summary>
                                        <pre className="bg-light p-3 rounded-3 mt-2 text-dark border overflow-auto" style={{ maxHeight: '250px', fontSize: '0.78rem' }}>
                                            {JSON.stringify(rawResponse, null, 2)}
                                        </pre>
                                    </details>
                                </>
                            ) : (
                                <div className="d-flex flex-column align-items-center justify-content-center py-5 text-center text-muted border border-dashed rounded-4">
                                    <Icon name="truck" className="fs-1 mb-3 text-muted text-opacity-50" />
                                    <div className="small">Sélectionne un véhicule puis clique sur calculer.</div>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}

            <style dangerouslySetInnerHTML={{ __html: `
                .extra-small { font-size: 0.72rem; }
                .cursor-pointer { cursor: pointer; }
                .bg-light { background-color: #f8f9fa !important; }
                .active-sub-tab {
                    background: rgba(52, 152, 219, 0.08) !important;
                    border-color: var(--acc-accent) !important;
                }
            `}} />
        </div>
    );
};

export default ProfitabilityTab;
